/**
 * Mesin hitung pajak — **fungsi murni**, tanpa I/O.
 *
 * Sumber: `design/SCHEMA-v0.md` §8 · K10 · K11 · P1 · P4.
 *
 * ### Kenapa di `packages/domain`, bukan di `portal-api` maupun `contract`
 * - **Bukan `portal-api`:** outlet wajib bisa menghitung pajak **saat internet putus** (K3).
 *   Mesin yang hanya hidup di portal akan menjadikan pajak sebagai layanan jaringan — dan itu
 *   melanggar janji produk paling dasar.
 * - **Bukan `contract`:** kontrak memuat **bentuk & validasi kawat**, bukan kalkulasi. Mencampurnya
 *   membuat paket yang seharusnya kecil dan sangat stabil ikut berubah tiap aturan pajak bergeser.
 *
 * ### ⭐ Keputusan pembulatan: HITUNG TOTAL SEKALI, LALU ALOKASIKAN
 * Alternatifnya — membulatkan pajak tiap baris lalu menjumlahkannya — menghasilkan struk yang
 * **jumlah barisnya tidak sama dengan totalnya**. Contoh nyata (uji T3): 3 baris @10.000 dengan
 * diskon pesanan 1.000 ⇒ DPP baris 9.666,6666 / 9.666,6667 / 9.666,6667; PB1 10% per baris
 * dibulatkan = 2.900,0001, sementara 29.000 × 10% = 2.900,0000. Selisih 0,0001 itu kecil, tapi ia
 * **muncul di dokumen yang dibaca manusia** dan menjadikan invarian `tax_total = Σ baris`
 * (ditegakkan `parseEvent`) mustahil dipenuhi.
 *
 * ⇒ Per kode pajak: kumpulkan DPP tiap baris → jumlahkan → hitung pajak **sekali** → sebar kembali
 * dengan {@link allocateMoney} (sisa terbesar). Σ baris = total, **eksak menurut konstruksi**.
 *
 * @module
 */
import { addMoney, addRate, allocateMoney, cmpRate, isZeroMoney, money, mulDivMoney, mulMoneyByRate, rate as makeRate, subMoney, sumMoney, tryRate, } from '@osbpos/contract';
/** Mode pembulatan tunggal untuk seluruh perhitungan pajak. Eksplisit, tak pernah tersirat. */
const ROUNDING = 'half_up';
const ZERO = money('0');
const ONE_RATE = makeRate('1.000000');
/** Kesalahan setelan pajak. Setelan yang salah harus **berisik**, tak pernah diam-diam jadi nol. */
export class TaxConfigError extends Error {
    name = 'TaxConfigError';
}
export function computeOrderTax(input) {
    const codes = validateCodes(input.taxCodes);
    /* ── 1. Nilai bersih per baris ──────────────────────────────────────────── */
    const gross = sumMoney(input.lines.map((l) => l.grossAmount));
    const lineDiscounts = sumMoney(input.lines.map((l) => l.discountAmount));
    const afterLine = input.lines.map((l) => subMoney(l.grossAmount, l.discountAmount));
    // Potongan pesanan disebar dengan sisa-terbesar ⇒ Σ bagian = total, EKSAK (tak ada rupiah
    // yang hilang atau tercipta di antara baris).
    const discountShare = isZeroMoney(input.orderDiscount)
        ? afterLine.map(() => ZERO)
        : allocateMoney(input.orderDiscount, afterLine);
    const nets = afterLine.map((v, i) => subMoney(v, discountShare[i] ?? ZERO));
    /* ── 2. Pajak per kode: total sekali, lalu dialokasikan ─────────────────── */
    // Baris cuma-cuma tak ikut sama sekali (K10) — bukan dihitung lalu dinolkan.
    const kena = input.lines.map((l) => l.exemptReason === undefined);
    const rows = input.lines.map(() => []);
    /**
     * Apakah kode `c` berlaku untuk baris ke-`i`?
     *
     * ⛔ Cuma-cuma (K10) menang atas segalanya: baris yang diberikan gratis tak kena pajak apa pun,
     * walau grup pajaknya menyebut kodenya.
     */
    const berlaku = (kode, i) => {
        if (kena[i] !== true)
            return false;
        const daftar = input.lines[i]?.applicableTaxCodes;
        return daftar === undefined || daftar.includes(kode);
    };
    /*
     * ⛔⛔ **PAJAK GANDA** (Imron, 2026-08-03) — lapis TERAKHIR, dan sengaja berisik.
     *
     * PB1 (`local_tax`, pajak daerah atas barang & jasa tertentu) dan PPN (`vat`, pusat) mengenai
     * objek yang sama secara **eksklusif**. Menagih keduanya pada satu baris berarti memungut pajak
     * yang tak terutang dari tamu — dan uang itu tak bisa dikembalikan kepada orang yang sudah pulang.
     *
     * ⚠️ Portal sudah menolak grup pajak yang memuat keduanya. Lapis ini tetap ada karena data bisa
     * masuk lewat SQL, importir, atau tenant yang skemanya lebih tua. Kalau ia diam, hasilnya adalah
     * struk yang tiap angkanya benar secara aritmetika — jadi tak seorang pun menemukannya sampai
     * kantor pajak bertanya.
     *
     * ⭐ Yang dilarang adalah dua rezim pada SATU baris, bukan dalam satu bill: toko yang menjual
     * makanan (PB1) dan barang kemasan (PPN) di satu struk itu normal.
     */
    input.lines.forEach((l, i) => {
        if (kena[i] !== true)
            return;
        const rezim = codes
            .filter((c) => berlaku(c.taxCode, i))
            .filter((c) => c.kind === 'vat' || c.kind === 'local_tax');
        const jenis = new Set(rezim.map((c) => c.kind));
        if (jenis.size > 1) {
            throw new TaxConfigError(`baris ${l.lineId} kena PPN (vat) DAN PB1 (local_tax) sekaligus — pajak ganda: ` +
                `${rezim.map((c) => c.taxCode).join(' + ')}. Keduanya eksklusif; pisahkan produknya ` +
                'ke kelas fiskal yang berbeda.');
        }
    });
    for (const c of codes) {
        /*
         * ⚠️ Baris yang tak ikut memberi DPP nol — dan `allocateMoney` di bawah karena itu tak pernah
         * mendaratkan satu rupiah pun ke sana. Itulah yang menjaga invarian §8 `tax_total = Σ baris`
         * tetap eksak sekaligus membuat baris yang bebas benar-benar bebas.
         */
        const bases = input.lines.map((_, i) => berlaku(c.taxCode, i) ? baseForLine(c, nets[i] ?? ZERO, rows[i] ?? []) : ZERO);
        const totalBase = sumMoney(bases);
        if (isZeroMoney(totalBase))
            continue;
        const totalTax = c.isInclusive
            ? // Harga sudah termasuk pajak ⇒ urai: pajak = dasar × r / (1 + r).
                mulDivMoney(totalBase, c.rateValue, addRate(ONE_RATE, c.rateValue), ROUNDING)
            : mulMoneyByRate(totalBase, c.rateValue, ROUNDING);
        const bagian = allocateMoney(totalTax, bases);
        input.lines.forEach((_, i) => {
            if (!berlaku(c.taxCode, i))
                return;
            const base = bases[i] ?? ZERO;
            if (isZeroMoney(base))
                return;
            const amount = bagian[i] ?? ZERO;
            (rows[i] ??= []).push({
                taxCode: c.taxCode,
                name: c.name,
                kind: c.kind,
                rate: c.rate,
                isInclusive: c.isInclusive,
                // Pada pajak inklusif, DPP = dasar − pajak (pajaknya tadi ada DI DALAM dasar).
                taxableBase: c.isInclusive ? subMoney(base, amount) : base,
                taxAmount: amount,
            });
        });
    }
    /* ── 3. Rollup — Σ BARIS, tak pernah dihitung ulang dari subtotal × tarif ── */
    const all = rows.flat();
    const serviceChargeTotal = sumMoney(all.filter((r) => r.kind === 'service_charge').map((r) => r.taxAmount));
    const taxTotal = sumMoney(all.filter((r) => r.kind !== 'service_charge').map((r) => r.taxAmount));
    const eksklusif = sumMoney(all.filter((r) => !r.isInclusive).map((r) => r.taxAmount));
    const discountTotal = addMoney(lineDiscounts, input.orderDiscount);
    return {
        lines: input.lines.map((l, i) => {
            const result = {
                lineId: l.lineId,
                netAmount: nets[i] ?? ZERO,
                taxes: rows[i] ?? [],
            };
            return l.exemptReason === undefined ? result : { ...result, exemptReason: l.exemptReason };
        }),
        subtotalGross: gross,
        discountTotal,
        serviceChargeTotal,
        taxTotal,
        // Baris INKLUSIF menyumbang nol — nilainya sudah ada di dalam `subtotal_gross`.
        grandTotal: addMoney(subMoney(gross, discountTotal), eksklusif),
    };
}
function validateCodes(specs) {
    const seq = [...specs].sort((a, b) => a.sequence - b.sequence);
    const dikenal = new Set();
    const out = [];
    for (const c of seq) {
        const r = tryRate(c.rate);
        if (r === undefined) {
            throw new TaxConfigError(`tax_code ${c.taxCode}: rate "${c.rate}" bukan RATE yang sah`);
        }
        // ⚠️ "10" yang dimaksud 10% adalah kesalahan termahal di seluruh setelan pajak: ia lolos tipe,
        // lolos review, dan baru terlihat saat totalnya sebelas kali lipat.
        if (cmpRate(r, ONE_RATE) >= 0) {
            throw new TaxConfigError(`tax_code ${c.taxCode}: rate ${c.rate} harus PECAHAN 0 ≤ r < 1 ("0.100000" = 10%, bukan "10")`);
        }
        /*
         * ⭐⭐⭐ **Dasar yang MUSTAHIL berisi = setelan salah, bukan nol** (P8).
         *
         * ⛔ Mesin ini sudah lama menolak `tax:<code>` yang menunjuk kode tak dikenal — tapi
         * `service_charge` yang tak akan pernah terisi lolos begitu saja. Lubang yang sama, komponen
         * yang berbeda, dan akibatnya ditemukan di setelan sungguhan (tenant `arena`, 2026-08-06):
         *
         *   PB1 | kind=local_tax | 10% | seq=30 | base=[net_sales, service_charge]
         *   SC  | kind=local_tax |  7% | seq=30 | base=[service_charge]
         *
         * SC diberi `kind = local_tax` (bukan `service_charge`), jadi tak satu pun baris pernah
         * dihitung sebagai service charge — dan SC yang dikenakan DI ATAS service charge selalu nol.
         * Bill Rp100.000 keluar Rp110.000, bukan Rp117.700: **kurang Rp7.700 tiap Rp100.000**, di
         * setiap struk, tanpa satu pun galat.
         */
        const adaSC = specs.some((x) => x.kind === 'service_charge');
        const scLebihDulu = specs.some((x) => x.kind === 'service_charge' && x.sequence < c.sequence);
        if (c.baseComponents.includes('service_charge')) {
            /* ⛔ Dikenakan di atas DIRINYA SENDIRI ⇒ selalu nol. */
            if (c.kind === 'service_charge') {
                throw new TaxConfigError(`tax_code ${c.taxCode}: kind "service_charge" tapi dikenakan DI ATAS service_charge — ` +
                    'ia mengenai dirinya sendiri, jadi hasilnya selalu nol. Dasar yang benar biasanya ' +
                    '`net_sales`');
            }
            /*
             * ⛔ Tak ada satu pun kode ber-`kind = service_charge`, DAN tak ada komponen lain yang bisa
             * berisi ⇒ pajak ini selalu nol. ⚠️ Kalau masih ada `net_sales`, ini SAH: outlet yang memang
             * tak memungut service charge tak boleh berhenti berjualan (T23).
             */
            if (!adaSC && c.baseComponents.length === 1) {
                throw new TaxConfigError(`tax_code ${c.taxCode}: dasar pengenaannya HANYA "service_charge" sementara tak ada ` +
                    'kode pajak ber-kind `service_charge` — hasilnya selalu nol. Periksa `tax_kind` kode ' +
                    'service charge Anda');
            }
            /*
             * ⛔ SC ada tapi dihitung SESUDAH kode ini ⇒ DPP-nya kehilangan service charge. Aturan yang
             * sama persis dengan `tax:<code>` di bawah — cuma komponennya yang berbeda.
             */
            if (adaSC && !scLebihDulu) {
                throw new TaxConfigError(`tax_code ${c.taxCode}: dasar memuat "service_charge" tapi kode service charge-nya ` +
                    'ber-sequence TIDAK lebih kecil — ia akan dihitung saat service charge belum ada, ' +
                    'dan pajaknya kurang bayar');
            }
        }
        for (const comp of c.baseComponents) {
            if (comp === 'net_sales' || comp === 'service_charge')
                continue;
            if (comp.startsWith('tax:')) {
                const ref = comp.slice(4);
                // P8: dasar pengenaan yang menunjuk pajak tak dikenal berarti setelannya salah. Pajak yang
                // diam-diam jadi 0 adalah kegagalan senyap yang baru terlihat saat kantor pajak bertanya.
                if (!dikenal.has(ref)) {
                    throw new TaxConfigError(`tax_code ${c.taxCode}: base_components menunjuk "${ref}" yang tak dikenal ` +
                        'atau sequence-nya tidak lebih kecil');
                }
                continue;
            }
            throw new TaxConfigError(`tax_code ${c.taxCode}: komponen dasar "${comp}" tak dikenal`);
        }
        dikenal.add(c.taxCode);
        out.push({ ...c, rateValue: r });
    }
    return out;
}
/** DPP satu baris untuk satu kode pajak, dari komponen yang sudah dihitung sebelumnya. */
function baseForLine(c, net, sudah) {
    let total = ZERO;
    for (const comp of c.baseComponents) {
        if (comp === 'net_sales') {
            total = addMoney(total, net);
        }
        else if (comp === 'service_charge') {
            total = addMoney(total, sumMoney(sudah.filter((r) => r.kind === 'service_charge').map((r) => r.taxAmount)));
        }
        else {
            const ref = comp.slice(4);
            total = addMoney(total, sumMoney(sudah.filter((r) => r.taxCode === ref).map((r) => r.taxAmount)));
        }
    }
    return total;
}
//# sourceMappingURL=tax-engine.js.map